import React, { useState } from 'react';
import dayjs from 'dayjs';
import { MenuItem, TextField } from '@mui/material';
import { DocumentFormPopup, FormSection, Field, FieldPair, DateField, formStyles as styles } from './DocumentFormPopup';
import InsuranceDocumentFilesField from '../../../../../components/insurance-documents/InsuranceDocumentFilesField';
import { MEDICAL_REQUEST_CONTEXT } from '../../../../../components/insurance-documents/InsuranceDocumentAttachments';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { releaseUploadItem, uploadAttachmentItems } from '../../../../../popup/PatientAttachmentUploadPopup/uploadAttachments';
import {
  DEFAULT_REQUEST_VALIDITY, REQUEST_VALIDITY_OPTIONS, formatDate, fromApiDate, isIncompleteDate, isValidDate,
  presetFromValidity, toApiDate, validityFromPreset,
} from './documentFormat';

const NOTES_MAX = 500;

const hasValue = (value) => value !== undefined && value !== null && value !== '';

/**
 * Criar ou editar um pedido médico. O popup guarda o próprio estado e valida;
 * `onSave(values)` devolve uma Promise com { ok, error, id } (quem grava é o dono).
 *
 * Com `patientId`, o formulário ganha o campo "Anexar pedido" (foto ou PDF):
 * os arquivos sobem depois de gravar, com o id devolvido por `onSave` (no
 * "Editar", o id do próprio pedido). Se algum arquivo falhar, o pedido já está
 * salvo: o popup fica aberto só com o que falhou e "Salvar" tenta de novo,
 * sem gravar o pedido outra vez.
 *
 * `onSaved(saved)` (opcional) roda depois de tudo, antes de fechar, com o
 * pedido gravado: { id, title, requesting_professional, issued_at,
 * valid_until, notes, attachments_uploaded }.
 */
function MedicalRequestFormPopup({ request, patientId, onSave, onSaved, onClose }) {
  const isEdit = !!request;
  const { UploadPatientAttachment } = DataProviderInstance();
  const canAttach = hasValue(patientId);

  const [form, setForm] = useState({
    title: request?.title || '',
    requesting_professional: request?.requesting_professional || '',
    issued_at: isEdit ? fromApiDate(request.issued_at) : dayjs(),
    validity: isEdit ? presetFromValidity(request.valid_until) : DEFAULT_REQUEST_VALIDITY,
    valid_until: fromApiDate(request?.valid_until),
    notes: request?.notes || '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState('');
  // Pedido já gravado cujos anexos falharam: o próximo "Salvar" só reenvia os arquivos
  const [saved, setSaved] = useState(null);

  const change = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] || prev.general ? { ...prev, [name]: undefined, general: undefined } : prev));
  };

  // Prazo pronto: o vencimento sai da emissão e acompanha a troca dela
  const isCustom = form.validity === 'custom';
  const validUntil = isCustom ? form.valid_until : validityFromPreset(form.issued_at, form.validity);

  const changeValidity = (value) => {
    // Ao passar para "Outra data", o calendário já abre com o vencimento que estava valendo
    if (value === 'custom' && !form.valid_until) change('valid_until', validUntil);
    change('validity', value);
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim()) next.title = 'Informe o título do pedido';
    if (!isValidDate(form.issued_at)) next.issued_at = 'Informe a data de emissão';
    if (isCustom && !form.valid_until) {
      next.valid_until = 'Informe a data de vencimento';
    } else if (isCustom && isIncompleteDate(form.valid_until)) {
      next.valid_until = 'Data incompleta';
    } else if (isCustom && isValidDate(form.valid_until) && isValidDate(form.issued_at) && dayjs(form.valid_until).isBefore(dayjs(form.issued_at), 'day')) {
      next.valid_until = 'O vencimento não pode ser anterior à emissão';
    }
    if (form.notes.length > NOTES_MAX) next.notes = `Até ${NOTES_MAX} caracteres`;
    return next;
  };

  const submit = async () => {
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setSaving(true);
    let current = saved;
    if (!current) {
      const values = {
        title: form.title.trim(),
        requesting_professional: form.requesting_professional.trim(),
        issued_at: toApiDate(form.issued_at),
        valid_until: toApiDate(validUntil),
        notes: form.notes.trim(),
      };
      const result = await onSave(values);
      if (!result?.ok) {
        setSaving(false);
        setErrors({ general: result?.error || 'Não foi possível salvar o pedido. Tente de novo.' });
        return;
      }
      current = { ...values, id: hasValue(result.id) ? result.id : request?.id ?? null, attachments_uploaded: 0 };
    }

    const done = await uploadFiles(current);
    setSaving(false);
    if (!done) return;

    if (onSaved) onSaved(done);
    onClose();
  };

  /**
   * Envia os arquivos escolhidos com o id do pedido já gravado.
   * @returns {Promise<object|null>} o pedido gravado, ou null se sobrou arquivo por enviar
   */
  const uploadFiles = async (current) => {
    if (!canAttach || files.length === 0) return current;

    if (!hasValue(current.id)) {
      setSaved(current);
      setErrors({ general: 'O pedido foi salvo, mas o anexo não foi enviado. Anexe pela ficha da pessoa.' });
      return null;
    }

    // A data do anexo é a da emissão (o envio não aceita data futura)
    const issued = dayjs(current.issued_at);
    const documentDate = issued.isValid() && !issued.isAfter(dayjs(), 'day') ? current.issued_at : dayjs().format('YYYY-MM-DD');

    const result = await uploadAttachmentItems(UploadPatientAttachment, files, {
      patientId,
      context: MEDICAL_REQUEST_CONTEXT,
      contextId: current.id,
      documentDate,
    }, {
      buildItemPayload: () => ({ title: current.title }),
      onProgress: (position, total) => setProgress(`Enviando anexo ${position} de ${total}…`),
    });
    setProgress('');

    const next = { ...current, attachments_uploaded: (current.attachments_uploaded || 0) + result.success };
    if (result.failed > 0) {
      setFiles(result.failedItems);
      setSaved(next);
      setErrors({ general: `O pedido foi salvo, mas ${result.failed === 1 ? 'um arquivo não foi enviado' : `${result.failed} arquivos não foram enviados`}. ${result.lastError || ''} Salve de novo para tentar enviar.`.trim() });
      return null;
    }

    files.forEach(releaseUploadItem);
    setFiles([]);
    return next;
  };

  // Fechar sem salvar: o pedido gravado (anexo pendente) ainda avisa o dono
  const close = () => {
    files.forEach(releaseUploadItem);
    if (saved && onSaved) onSaved(saved);
    onClose();
  };

  const locked = saving || !!saved;

  // Linha abaixo do prazo: a data calculada; em "Outra data" o calendário ocupa o lugar
  let validityHint;
  if (form.validity === 'none') validityHint = 'Sem aviso de vencimento';
  else if (!isCustom && validUntil) validityHint = `Vence em ${formatDate(validUntil)}`;

  return (
    <DocumentFormPopup
      title={isEdit ? 'Editar pedido médico' : 'Novo pedido médico'}
      submitLabel={saved ? 'Enviar anexo de novo' : (isEdit ? 'Salvar' : 'Registrar pedido')}
      saving={saving}
      generalError={errors.general}
      onSubmit={submit}
      onClose={close}
    >
      <FormSection title="Pedido">
        <Field label="Título *" htmlFor="medical-request-title">
          <TextField
            id="medical-request-title"
            value={form.title}
            onChange={(e) => change('title', e.target.value)}
            disabled={locked}
            fullWidth
            size="small"
            autoFocus={!isEdit}
            placeholder="Ex.: 10 sessões de fisioterapia, Avaliação nutricional"
            error={!!errors.title}
            helperText={errors.title}
            className={styles.input}
          />
        </Field>
        <Field label="Quem pediu" htmlFor="medical-request-professional">
          <TextField
            id="medical-request-professional"
            value={form.requesting_professional}
            onChange={(e) => change('requesting_professional', e.target.value)}
            disabled={locked}
            fullWidth
            size="small"
            placeholder="Nome do profissional que emitiu o pedido"
            className={styles.input}
          />
        </Field>
      </FormSection>

      <FormSection title="Datas">
        <FieldPair>
          <Field label="Emitido em *" htmlFor="medical-request-issued">
            <DateField
              id="medical-request-issued"
              value={form.issued_at}
              onChange={(value) => change('issued_at', value)}
              disabled={locked}
              error={errors.issued_at}
            />
          </Field>
          <Field label="Vencimento em" htmlFor="medical-request-validity">
            <TextField
              id="medical-request-validity"
              value={form.validity}
              onChange={(e) => changeValidity(e.target.value)}
              disabled={locked}
              fullWidth
              size="small"
              select
              helperText={validityHint}
              className={styles.input}
            >
              {REQUEST_VALIDITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            {/* Outra data: o calendário fica logo abaixo do seletor, na mesma coluna */}
            {isCustom && (
              <DateField
                id="medical-request-valid-until"
                ariaLabel="Data do vencimento"
                value={form.valid_until}
                onChange={(value) => change('valid_until', value)}
                disabled={locked}
                minDate={form.issued_at || undefined}
                error={errors.valid_until}
              />
            )}
          </Field>
        </FieldPair>
      </FormSection>

      {canAttach && (
        <FormSection title="Anexar pedido">
          <InsuranceDocumentFilesField
            patientId={patientId}
            context={MEDICAL_REQUEST_CONTEXT}
            contextId={isEdit ? request.id : saved?.id}
            items={files}
            onItemsChange={setFiles}
            disabled={saving}
            onError={(message) => setErrors((prev) => ({ ...prev, general: message }))}
          />
          {progress && <span className={styles.hint}>{progress}</span>}
        </FormSection>
      )}

      <FormSection title="Observações">
        <Field htmlFor="medical-request-notes">
          <TextField
            id="medical-request-notes"
            value={form.notes}
            onChange={(e) => change('notes', e.target.value)}
            disabled={locked}
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Opcional. CID, orientações, o que foi solicitado"
            error={!!errors.notes}
            helperText={errors.notes || `${form.notes.length}/${NOTES_MAX}`}
            className={styles.input}
          />
        </Field>
      </FormSection>
    </DocumentFormPopup>
  );
}

export default MedicalRequestFormPopup;
